import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Agent, AgentTemplate, McpServerConfig, SessionSummary, SessionSource, ConversationMetrics, WhatsappConfig, EazyshowConfig } from '../types';
import { getAgent, updateAgent, deleteAgent, listAgentSessions, getPublicConfig, testAgentMcp, listTemplates, listKnowledgeBases, KnowledgeBase, API_BASE_URL } from '../api/client';
import { MetricsPanel } from './MetricsPanel';
import { MarkdownText } from './MarkdownText';

export function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [greeting, setGreeting] = useState('');
  const [maxHistoryMessages, setMaxHistoryMessages] = useState<number | undefined>(undefined);
  const [detailedMetrics, setDetailedMetrics] = useState(false);
  // '' = inherit the template's voice mode (saved as null to clear the override).
  const [voiceMode, setVoiceMode] = useState<'pipeline' | 'realtime' | ''>('');
  const [mcpServers, setMcpServers] = useState<McpServerConfig[]>([]);
  const [mcpTestResult, setMcpTestResult] = useState<string | null>(null);
  const [mcpTesting, setMcpTesting] = useState(false);
  const [twilioBaseUrl, setTwilioBaseUrl] = useState('');
  const [backendUrl, setBackendUrl] = useState('');
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [knowledgeBaseIds, setKnowledgeBaseIds] = useState<string[]>([]);
  const [whatsapp, setWhatsapp] = useState<WhatsappConfig>({ phoneNumberId: '', accessToken: '' });
  const [eazyshow, setEazyshow] = useState<EazyshowConfig>({ baseUrl: '', s2sToken: '', accountId: '', webhookToken: '' });
  const [photoUploadTool, setPhotoUploadTool] = useState('');

  useEffect(() => {
    if (agentId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const loadData = async () => {
    if (!agentId) return;
    try {
      setLoading(true);
      const [a, sess, cfg, allTemplates, bases] = await Promise.all([getAgent(agentId), listAgentSessions(agentId), getPublicConfig(), listTemplates(), listKnowledgeBases().catch(() => [])]);
      setAgent(a);
      setKnowledgeBases(bases);
      setKnowledgeBaseIds(a.knowledgeBaseIds || []);
      setTemplates(allTemplates);
      setTemplateId(a.templateId);
      setName(a.name);
      setCustomPrompt(a.customPrompt || '');
      setGreeting(a.greeting || '');
      setMaxHistoryMessages(a.maxHistoryMessages);
      setDetailedMetrics(a.detailedMetrics || false);
      setVoiceMode(a.voiceMode || '');
      setMcpServers(a.mcpServers || []);
      setWhatsapp(a.whatsapp || { phoneNumberId: '', accessToken: '' });
      setEazyshow(a.eazyshow || { baseUrl: '', s2sToken: '', accountId: '', webhookToken: '' });
      setPhotoUploadTool(a.mediaUpload?.toolName || '');
      setTwilioBaseUrl(cfg.twilioBaseUrl);
      setBackendUrl(cfg.backendUrl);
      setSessions(sess);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!agentId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateAgent(agentId, {
        name, templateId, customPrompt, greeting,
        maxHistoryMessages: maxHistoryMessages || undefined,
        detailedMetrics,
        // null (not undefined) so the backend clears the override — undefined is filtered out.
        voiceMode: voiceMode || null,
        mcpServers: mcpServers.filter((s) => s.name && s.url),
        knowledgeBaseIds,
        // The channel needs both to work at all, so a half-filled form saves nothing.
        whatsapp: whatsapp.phoneNumberId && whatsapp.accessToken ? whatsapp : undefined,
        eazyshow: eazyshow.baseUrl && eazyshow.s2sToken && eazyshow.accountId ? eazyshow : undefined,
        mediaUpload: photoUploadTool.trim() ? { toolName: photoUploadTool.trim() } : undefined,
      });
      setAgent(updated);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!agentId || !window.confirm('Delete this agent?')) return;
    await deleteAgent(agentId);
    navigate('/agents');
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  const addMcpServer = () => setMcpServers([...mcpServers, { name: '', url: '', auth: { type: 'none' } }]);
  const updateMcpServer = (i: number, u: Partial<McpServerConfig>) => setMcpServers(mcpServers.map((srv, j) => j === i ? { ...srv, ...u } : srv));
  const removeMcpServer = (i: number) => setMcpServers(mcpServers.filter((_, j) => j !== i));

  const toggleKnowledgeBase = (kbId: string) =>
    setKnowledgeBaseIds((prev) =>
      prev.includes(kbId) ? prev.filter((id) => id !== kbId) : [...prev, kbId],
    );

  const handleTestMcp = async () => {
    if (!agentId) return;
    setMcpTesting(true);
    setMcpTestResult(null);
    try {
      const result = await testAgentMcp(agentId);
      setMcpTestResult(`${result.toolCount} tools from ${result.serverCount} servers: ${result.tools.map((t) => t.name).join(', ')}`);
    } catch (e: any) {
      setMcpTestResult(`Error: ${e.message}`);
    } finally {
      setMcpTesting(false);
    }
  };

  if (loading) return <div style={s.container}><div style={s.muted}>Loading...</div></div>;
  if (!agent) return <div style={s.container}><div style={s.error}>Agent not found</div></div>;

  const baseUrl = twilioBaseUrl || '<your-public-url>';
  const twilioUrl = `${baseUrl}/api/twilio/voice/${agent.id}`;
  const twilioStatusUrl = `${baseUrl}/api/twilio/status`;
  const wsUrl = `${baseUrl.replace('https://', 'wss://')}/media-stream/${agent.id}`;
  const restUrl = `${backendUrl || API_BASE_URL}/conversation/agent-process`;
  // Pasted into Meta App → WhatsApp → Configuration as the Callback URL.
  const whatsappWebhookUrl = `${backendUrl || API_BASE_URL}/whatsapp/webhook/${agent.id}`;
  // Eazyshow posts operator actions here — one URL per instance, not per agent:
  // it goes into their CONVERSATIONS_SERVER_URL, and the agent is resolved from
  // the account id in the request.
  const eazyshowWebhookUrl = `${backendUrl || API_BASE_URL}/eazyshow/webhook`;
  // Status reflects what is SAVED on the agent, not what is typed in the form —
  // otherwise a half-filled form reads as configured before anything is stored.
  const whatsappConfigured = !!(agent.whatsapp?.phoneNumberId && agent.whatsapp?.accessToken);
  const eazyshowBridged = !!(agent.eazyshow?.baseUrl && agent.eazyshow?.accountId && agent.eazyshow?.s2sToken);
  // Base URL a consumer (Eazyshow / live-video-chat) pastes into its settings —
  // the standard Mastra drop-in base (API_BASE_URL ends with /api → +/std).
  const eazyshowIntegrationUrl = `${API_BASE_URL}/std`;

  return (
    <div style={s.container}>
      <button style={s.backBtn} onClick={() => navigate('/agents')}>&larr; Back</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ ...s.heading, margin: 0 }}>{agent.name}</h2>
        <button
          style={s.saveBtn}
          onClick={() => navigate('/chat', { state: { apiKey: agent.apiKey } })}
        >
          Connect
        </button>
      </div>

      {/* Integration URLs */}
      <div style={s.section}>
        <h3 style={s.sub}>Integration</h3>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>API Key</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{agent.apiKey}</code>
            <button style={s.copyBtn} onClick={() => copy(agent.apiKey)}>Copy</button>
          </div>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>Agent ID</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{agent.id}</code>
            <button style={s.copyBtn} onClick={() => copy(agent.id)}>Copy</button>
          </div>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>Eazyshow Integration URL</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{eazyshowIntegrationUrl}</code>
            <button style={s.copyBtn} onClick={() => copy(eazyshowIntegrationUrl)}>Copy</button>
          </div>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>Twilio Voice Webhook</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{twilioUrl}</code>
            <button style={s.copyBtn} onClick={() => copy(twilioUrl)}>Copy</button>
          </div>
          <span style={s.urlHint}>{twilioBaseUrl ? 'Method: POST' : 'Set TWILIO_BASE_URL env var to auto-fill. Method: POST'}</span>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>Twilio Status Callback</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{twilioStatusUrl}</code>
            <button style={s.copyBtn} onClick={() => copy(twilioStatusUrl)}>Copy</button>
          </div>
          <span style={s.urlHint}>Set as "Status Callback URL" in Twilio phone number config. Used for session cleanup.</span>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>WhatsApp Callback URL (Meta)</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{whatsappWebhookUrl}</code>
            <button style={s.copyBtn} onClick={() => copy(whatsappWebhookUrl)}>Copy</button>
          </div>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>WebSocket Media Stream</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{wsUrl}</code>
            <button style={s.copyBtn} onClick={() => copy(wsUrl)}>Copy</button>
          </div>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>TwiML Example (Callback Integration)</label>
          <pre style={s.twimlCode}>{'<Response>\n  <Connect>\n    <Stream url="' + wsUrl + '">\n      <Parameter name="sessionId" value="{sessionId}" />\n      <Parameter name="sessionToken" value="{sessionToken}" />\n    </Stream>\n  </Connect>\n</Response>'}</pre>
          <button style={s.copyBtn} onClick={() => copy('<Response>\n  <Connect>\n    <Stream url="' + wsUrl + '">\n      <Parameter name="sessionId" value="{sessionId}" />\n      <Parameter name="sessionToken" value="{sessionToken}" />\n    </Stream>\n  </Connect>\n</Response>')}>Copy TwiML</button>
          <span style={s.urlHint}>Replace {'{sessionId}'} and {'{sessionToken}'} with values from POST /api/agents/connect</span>
        </div>

        <div style={s.urlBlock}>
          <label style={s.urlLabel}>REST API Endpoint</label>
          <div style={s.urlRow}>
            <code style={s.urlCode}>{restUrl}</code>
            <button style={s.copyBtn} onClick={() => copy(restUrl)}>Copy</button>
          </div>
          <span style={s.urlHint}>Headers: x-agent-key, x-session-id. Body: multipart audio</span>
        </div>
      </div>

      {/* Settings */}
      <div style={s.section}>
        <h3 style={s.sub}>Settings</h3>
        <div style={s.form}>
          <label style={s.label}>Template
            <select style={s.input} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label style={s.label}>Name <input style={s.input} value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label style={s.label}>Greeting (override) <input style={s.input} value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Leave empty to use template greeting" /></label>
          <label style={s.label}>Voice mode (override) <select style={s.input} value={voiceMode} onChange={(e) => setVoiceMode(e.target.value as 'pipeline' | 'realtime' | '')}>
            <option value="">Inherit from template</option>
            <option value="pipeline">Pipeline (STT → LLM → TTS, provider metrics)</option>
            <option value="realtime">Realtime (speech-to-speech, low latency; STT/TTS ignored)</option>
          </select></label>
          <label style={s.label}>Additional Instructions <textarea style={s.textarea} value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} rows={4} placeholder="Extra context appended to system prompt" /></label>
          <label style={s.label}>Max History Messages <input style={s.input} type="number" min={2} max={100} value={maxHistoryMessages ?? ''} onChange={(e) => setMaxHistoryMessages(e.target.value ? Number(e.target.value) : undefined)} placeholder="Default: 10" /></label>
          <label style={{ ...s.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={detailedMetrics} onChange={(e) => setDetailedMetrics(e.target.checked)} />
            Detailed cost metrics
            <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>Save per-provider breakdown (STT/LLM/TTS) in session history</span>
          </label>

          {Object.keys(agent.customFieldValues).length > 0 && (
            <div>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Custom Fields</span>
              {Object.entries(agent.customFieldValues).map(([k, v]) => (
                <div key={k} style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  <strong>{k}:</strong> {v}
                </div>
              ))}
            </div>
          )}

          {/* MCP Servers */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>MCP Servers ({mcpServers.length})</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {mcpServers.length > 0 && (
                  <button style={s.smallBtn} onClick={handleTestMcp} disabled={mcpTesting}>
                    {mcpTesting ? 'Testing...' : 'Test'}
                  </button>
                )}
                <button style={s.smallBtn} onClick={addMcpServer}>+ Add</button>
              </div>
            </div>
            {mcpServers.map((srv, i) => (
              <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                  <input style={{ ...s.input, flex: 1, padding: '4px 8px', fontSize: 12 }} placeholder="Name" value={srv.name} onChange={(e) => updateMcpServer(i, { name: e.target.value })} />
                  <input style={{ ...s.input, flex: 2, padding: '4px 8px', fontSize: 12 }} placeholder="URL (http://...)" value={srv.url} onChange={(e) => updateMcpServer(i, { url: e.target.value })} />
                  <button style={s.delBtn} onClick={() => removeMcpServer(i)}>x</button>
                </div>
                {/* Headers */}
                <div style={{ marginBottom: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#888' }}>Headers</span>
                    <button style={{ ...s.smallBtn, fontSize: 10 }} onClick={() => {
                      const h = { ...(srv.headers || {}), '': '' };
                      updateMcpServer(i, { headers: h });
                    }}>+ Header</button>
                  </div>
                  {Object.entries(srv.headers || {}).map(([key, val], hi) => (
                    <div key={hi} style={{ display: 'flex', gap: 4, marginBottom: 3, alignItems: 'center' }}>
                      <input style={{ ...s.input, flex: 1, padding: '3px 6px', fontSize: 11 }} placeholder="Header name" value={key} onChange={(e) => {
                        const entries = Object.entries(srv.headers || {});
                        entries[hi] = [e.target.value, val];
                        updateMcpServer(i, { headers: Object.fromEntries(entries) });
                      }} />
                      <input style={{ ...s.input, flex: 2, padding: '3px 6px', fontSize: 11 }} placeholder="Value" type={key.toLowerCase().includes('secret') || key.toLowerCase().includes('key') || key.toLowerCase().includes('token') ? 'password' : 'text'} value={val} onChange={(e) => {
                        const h = { ...(srv.headers || {}) };
                        const entries = Object.entries(h);
                        entries[hi] = [key, e.target.value];
                        updateMcpServer(i, { headers: Object.fromEntries(entries) });
                      }} />
                      <button style={{ ...s.delBtn, padding: '2px 6px', fontSize: 10 }} onClick={() => {
                        const entries = Object.entries(srv.headers || {}).filter((_, j) => j !== hi);
                        updateMcpServer(i, { headers: Object.fromEntries(entries) });
                      }}>x</button>
                    </div>
                  ))}
                </div>
                {/* Auth */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select
                    style={{ ...s.input, padding: '3px 6px', fontSize: 11 }}
                    value={srv.auth?.type || 'none'}
                    onChange={(e) => updateMcpServer(i, { auth: { type: e.target.value as any } })}
                  >
                    <option value="none">No Auth</option>
                    <option value="client_credentials">Client Credentials</option>
                  </select>
                  {srv.auth?.type === 'client_credentials' && (
                    <>
                      <input style={{ ...s.input, flex: 1, padding: '3px 6px', fontSize: 11 }} placeholder="Client ID" value={srv.auth?.clientId || ''} onChange={(e) => updateMcpServer(i, { auth: { ...srv.auth!, clientId: e.target.value } })} />
                      <input style={{ ...s.input, flex: 1, padding: '3px 6px', fontSize: 11 }} placeholder="Client Secret" type="password" value={srv.auth?.clientSecret || ''} onChange={(e) => updateMcpServer(i, { auth: { ...srv.auth!, clientSecret: e.target.value } })} />
                    </>
                  )}
                </div>
                {/* Context Args — arg hidden from the model, injected from requestContext at call time */}
                <div style={{ marginTop: 6, marginBottom: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#888' }}>Context Args (tool arg → requestContext key)</span>
                    <button style={{ ...s.smallBtn, fontSize: 10 }} onClick={() => {
                      const c = { ...(srv.contextArgs || {}), '': '' };
                      updateMcpServer(i, { contextArgs: c });
                    }}>+ Arg</button>
                  </div>
                  {Object.entries(srv.contextArgs || {}).map(([key, val], ci) => (
                    <div key={ci} style={{ display: 'flex', gap: 4, marginBottom: 3, alignItems: 'center' }}>
                      <input style={{ ...s.input, flex: 1, padding: '3px 6px', fontSize: 11 }} placeholder="Tool arg (e.g. sessionId)" value={key} onChange={(e) => {
                        const entries = Object.entries(srv.contextArgs || {});
                        entries[ci] = [e.target.value, val];
                        updateMcpServer(i, { contextArgs: Object.fromEntries(entries) });
                      }} />
                      <input style={{ ...s.input, flex: 1, padding: '3px 6px', fontSize: 11 }} placeholder="Context key (e.g. navigatorSessionId)" value={val} onChange={(e) => {
                        const entries = Object.entries(srv.contextArgs || {});
                        entries[ci] = [key, e.target.value];
                        updateMcpServer(i, { contextArgs: Object.fromEntries(entries) });
                      }} />
                      <button style={{ ...s.delBtn, padding: '2px 6px', fontSize: 10 }} onClick={() => {
                        const entries = Object.entries(srv.contextArgs || {}).filter((_, j) => j !== ci);
                        updateMcpServer(i, { contextArgs: Object.fromEntries(entries) });
                      }}>x</button>
                    </div>
                  ))}
                </div>
                {/* Hidden Tools — dropped from this server before exposing to the agent */}
                <div style={{ marginBottom: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#888' }}>Hidden Tools</span>
                    <button style={{ ...s.smallBtn, fontSize: 10 }} onClick={() => updateMcpServer(i, { hiddenTools: [...(srv.hiddenTools || []), ''] })}>+ Tool</button>
                  </div>
                  {(srv.hiddenTools || []).map((tool, ti) => (
                    <div key={ti} style={{ display: 'flex', gap: 4, marginBottom: 3, alignItems: 'center' }}>
                      <input style={{ ...s.input, flex: 1, padding: '3px 6px', fontSize: 11 }} placeholder="Tool name (e.g. list_sessions)" value={tool} onChange={(e) => {
                        const arr = [...(srv.hiddenTools || [])];
                        arr[ti] = e.target.value;
                        updateMcpServer(i, { hiddenTools: arr });
                      }} />
                      <button style={{ ...s.delBtn, padding: '2px 6px', fontSize: 10 }} onClick={() => updateMcpServer(i, { hiddenTools: (srv.hiddenTools || []).filter((_, j) => j !== ti) })}>x</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <label style={{ ...s.label, fontSize: 11, color: '#888', marginTop: 4 }}>Photo upload tool
              <input
                style={{ ...s.input, padding: '4px 8px', fontSize: 12 }}
                placeholder="e.g. upload_vehicle_photo"
                value={photoUploadTool}
                onChange={(e) => setPhotoUploadTool(e.target.value)}
              />
            </label>
            {mcpTestResult && (
              <div style={{ fontSize: 12, color: mcpTestResult.startsWith('Error') ? '#dc2626' : '#059669', padding: '4px 0' }}>
                {mcpTestResult}
              </div>
            )}
          </div>

          {/* WhatsApp — per-agent Meta Cloud API credentials for its own number */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>WhatsApp (Meta Cloud API)</span>
              <span style={{ fontSize: 11, color: whatsappConfigured ? '#16a34a' : '#888' }}>
                {whatsappConfigured ? 'Configured' : 'Not configured'}
              </span>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Phone number ID
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} value={whatsapp.phoneNumberId} onChange={(e) => setWhatsapp({ ...whatsapp, phoneNumberId: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Access token (system user)
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} type="password" value={whatsapp.accessToken} onChange={(e) => setWhatsapp({ ...whatsapp, accessToken: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Verify token
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} value={whatsapp.verifyToken || ''} onChange={(e) => setWhatsapp({ ...whatsapp, verifyToken: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>App secret
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} type="password" value={whatsapp.appSecret || ''} onChange={(e) => setWhatsapp({ ...whatsapp, appSecret: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Callback URL
                <div style={s.urlRow}>
                  <code style={{ ...s.urlCode, fontSize: 11 }}>{whatsappWebhookUrl}</code>
                  <button style={s.copyBtn} onClick={() => copy(whatsappWebhookUrl)}>Copy</button>
                </div>
              </label>
            </div>
          </div>

          {/* Eazyshow — operators watch this agent's chats and can take over */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Eazyshow (operator handover)</span>
              <span style={{ fontSize: 11, color: eazyshowBridged ? '#16a34a' : '#888' }}>
                {eazyshowBridged ? 'Bridged' : 'Not bridged'}
              </span>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Instance URL
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} value={eazyshow.baseUrl} onChange={(e) => setEazyshow({ ...eazyshow, baseUrl: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Account ID
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} value={eazyshow.accountId} onChange={(e) => setEazyshow({ ...eazyshow, accountId: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>S2S token
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} type="password" value={eazyshow.s2sToken} onChange={(e) => setEazyshow({ ...eazyshow, s2sToken: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Webhook token
                <input style={{ ...s.input, padding: '4px 8px', fontSize: 12 }} type="password" value={eazyshow.webhookToken} onChange={(e) => setEazyshow({ ...eazyshow, webhookToken: e.target.value })} />
              </label>
              <label style={{ ...s.label, fontSize: 11, color: '#888' }}>Webhook URL
                <div style={s.urlRow}>
                  <code style={{ ...s.urlCode, fontSize: 11 }}>{eazyshowWebhookUrl}</code>
                  <button style={s.copyBtn} onClick={() => copy(eazyshowWebhookUrl)}>Copy</button>
                </div>
              </label>
            </div>
          </div>

          {/* Knowledge bases */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Knowledge Bases ({knowledgeBaseIds.length} attached)</span>
              <Link to="/knowledge" style={{ ...s.smallBtn, textDecoration: 'none' }}>Manage bases</Link>
            </div>
            {knowledgeBases.length === 0 ? (
              <div style={s.muted}>No knowledge bases yet — create one on the Knowledge page</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {knowledgeBases.map((base) => (
                  <label key={base.id} style={{ ...s.sessCard, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={knowledgeBaseIds.includes(base.id)}
                        onChange={() => toggleKnowledgeBase(base.id)}
                      />
                      <span style={{ fontSize: 13, color: '#333' }}>{base.name}</span>
                      <span style={{ fontSize: 11, color: '#888' }}>{base.docCount} docs</span>
                    </div>
                    {base.description && <span style={{ fontSize: 11, color: '#aaa' }}>{base.description}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && <div style={s.error}>{error}</div>}
          {success && <div style={s.success}>{success}</div>}
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button style={s.delBtn} onClick={handleDelete}>Delete Agent</button>
          </div>
        </div>
      </div>

      {/* Sessions */}
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ ...s.sub, margin: 0 }}>Sessions ({sessions.length})</h3>
          <button style={s.copyBtn} onClick={loadData}>Refresh</button>
        </div>
        {sessions.length === 0 ? <div style={s.muted}>No sessions yet</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sessions.map((sess) => (
              <div key={sess.id}>
                <div style={s.sessCard} onClick={() => setSelectedSession(selectedSession === sess.id ? null : sess.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ fontSize: 11 }}>{sess.id.slice(0, 8)}</code>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                      background: sess.source === SessionSource.Twilio ? '#dbeafe' : '#f0fdf4',
                      color: sess.source === SessionSource.Twilio ? '#2563eb' : '#16a34a' }}>{sess.source}</span>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                      background: sess.status === 'active' ? '#fef3c7' : '#f3f4f6',
                      color: sess.status === 'active' ? '#d97706' : '#6b7280' }}>{sess.status}</span>
                    <span style={{ fontSize: 12, color: '#888' }}>{(sess.messages?.length || 0) / 2 | 0} turns</span>
                  </div>
                  <span style={{ fontSize: 11, color: '#aaa' }}>{new Date(sess.createdAt).toLocaleString()}</span>
                </div>
                {selectedSession === sess.id && sess.messages?.length > 0 && (
                  <div style={s.sessMessages}>
                    {sess.messages.map((m, i) => (
                      <div key={i} style={{ marginBottom: 6, fontSize: 13 }}>
                        <strong style={{ color: m.role === 'user' ? '#2563eb' : '#16a34a', fontSize: 12 }}>{m.role}:</strong>{' '}
                        {m.role === 'assistant' ? <MarkdownText text={m.text} /> : m.text}
                        {m.metrics && !m.metrics.stt && (
                          <span style={{ fontSize: 10, color: '#999', marginLeft: 8 }}>
                            {m.metrics.totalLatencyMs}ms{m.metrics.totalCost > 0 ? ` | $${m.metrics.totalCost.toFixed(4)}` : ''}
                          </span>
                        )}
                        {m.metrics?.stt && m.metrics?.llm && m.metrics?.tts && (
                          <MetricsPanel metrics={{
                            stt: m.metrics.stt,
                            llm: m.metrics.llm,
                            tts: m.metrics.tts,
                            totalLatencyMs: m.metrics.totalLatencyMs,
                            totalCost: m.metrics.totalCost,
                            wer: m.metrics.wer,
                          } as ConversationMetrics} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: '#4f46e5', fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12 },
  heading: { margin: '0 0 16px', fontSize: 20, color: '#1a1a1a' },
  sub: { margin: '0 0 10px', fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  section: { marginBottom: 20, padding: '14px 18px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  label: { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, color: '#555', flex: 1 },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none' },
  textarea: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' },
  saveBtn: { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  smallBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#555' },
  delBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
  success: { padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#16a34a', fontSize: 13 },
  muted: { color: '#888', fontSize: 13 },
  urlBlock: { marginBottom: 10 },
  urlLabel: { fontSize: 12, color: '#555', fontWeight: 600, display: 'block', marginBottom: 3 },
  urlRow: { display: 'flex', gap: 8, alignItems: 'center' },
  urlCode: { flex: 1, background: '#e2e8f0', padding: '5px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', color: '#333' },
  twimlCode: { background: '#e2e8f0', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre', color: '#333', margin: '0 0 4px', overflowX: 'auto' } as React.CSSProperties,
  urlHint: { fontSize: 11, color: '#888', marginTop: 2, display: 'block' },
  copyBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#555', whiteSpace: 'nowrap' },
  sessCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  sessMessages: { padding: '10px 14px', background: '#f8f9fa', borderRadius: '0 0 8px 8px', border: '1px solid #e5e7eb', borderTop: 'none' },
};
